'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import Header from '@/components/Header';

// ─── Types ────────────────────────────────────────────────────────────────────
interface PropertyForm {
  title: string;
  description: string;
  type: string;
  location: string;
  city: string;
  country: string;
  bedrooms: number;
  bathrooms: number;
  maxGuests: number;
  amenities: string[];
  pricePerNight: number;
  cleaningFee: number;
  minNights: number;
  instantBook: boolean;
  photos: string[];
}

// ─── Constants ────────────────────────────────────────────────────────────────
const PROPERTY_TYPES = [
  { id: 'entire_home', label: 'Entire Home', icon: '🏠', desc: 'Guests have the whole place to themselves' },
  { id: 'private_room', label: 'Private Room', icon: '🛏️', desc: 'Guests have their own room in a shared home' },
  { id: 'villa', label: 'Villa', icon: '🏡', desc: 'A luxurious standalone property with premium amenities' },
  { id: 'apartment', label: 'Apartment', icon: '🏢', desc: 'A self-contained unit in a residential building' },
  { id: 'lodge', label: 'Lodge', icon: '🌿', desc: 'A nature retreat or eco-lodge experience' },
  { id: 'cottage', label: 'Cottage', icon: '🏘️', desc: 'A cozy, charming smaller property' },
];

const AMENITIES_LIST = [
  'WiFi', 'Air conditioning', 'Kitchen', 'Parking', 'Pool', 'Gym',
  'TV', 'Washer', 'Dryer', 'Hot tub', 'BBQ grill', 'Garden',
  'Security cameras', 'Smoke alarm', 'First aid kit', 'Fire extinguisher',
];

const STEPS = [
  { id: 1, label: 'Property Type', icon: '🏠' },
  { id: 2, label: 'Details', icon: '📋' },
  { id: 3, label: 'Amenities', icon: '✨' },
  { id: 4, label: 'Pricing', icon: '💰' },
  { id: 5, label: 'Review & Publish', icon: '🚀' },
];

const SAMPLE_PHOTOS = [
  { url: 'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=400&q=80', alt: 'Luxury villa exterior with pool' },
  { url: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=400&q=80', alt: 'Modern apartment living room' },
  { url: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400&q=80', alt: 'Bright bedroom with natural light' },
  { url: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&q=80', alt: 'Modern kitchen with island' },
];

// ─── Step 1: Property Type ────────────────────────────────────────────────────
function StepType({ form, setForm }: { form: PropertyForm; setForm: React.Dispatch<React.SetStateAction<PropertyForm>> }) {
  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-1" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
        What type of property are you listing?
      </h2>
      <p className="text-gray-500 text-sm mb-8">Choose the category that best describes your space.</p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {PROPERTY_TYPES.map((t) => (
          <button
            key={t.id}
            onClick={() => setForm((f) => ({ ...f, type: t.id }))}
            className={`p-5 rounded-2xl border-2 text-left transition-all hover:shadow-md ${
              form.type === t.id
                ? 'border-rose-500 bg-rose-50 shadow-sm'
                : 'border-gray-200 bg-white hover:border-rose-200'
            }`}
          >
            <div className="text-3xl mb-3">{t.icon}</div>
            <div className="font-bold text-gray-900 text-sm">{t.label}</div>
            <div className="text-xs text-gray-500 mt-1 leading-snug">{t.desc}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Step 2: Property Details ─────────────────────────────────────────────────
function StepDetails({ form, setForm }: { form: PropertyForm; setForm: React.Dispatch<React.SetStateAction<PropertyForm>> }) {
  const counter = (field: 'bedrooms' | 'bathrooms' | 'maxGuests', min: number) => (
    <div className="flex items-center gap-3">
      <button
        onClick={() => setForm((f) => ({ ...f, [field]: Math.max(min, f[field] - 1) }))}
        className="w-9 h-9 rounded-full border-2 border-gray-200 flex items-center justify-center text-gray-600 hover:border-rose-400 hover:text-rose-500 font-bold transition-all"
      >
        −
      </button>
      <span className="w-8 text-center font-bold text-gray-900">{form[field]}</span>
      <button
        onClick={() => setForm((f) => ({ ...f, [field]: f[field] + 1 }))}
        className="w-9 h-9 rounded-full border-2 border-gray-200 flex items-center justify-center text-gray-600 hover:border-rose-400 hover:text-rose-500 font-bold transition-all"
      >
        +
      </button>
    </div>
  );

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-1" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
        Tell us about your property
      </h2>
      <p className="text-gray-500 text-sm mb-8">Add the key details guests will see on your listing.</p>

      <div className="space-y-5">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Property Title *</label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            placeholder="e.g. Lakeside Villa with Private Pool"
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Description *</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Describe what makes your property special — the views, the vibe, nearby attractions..."
            rows={4}
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent resize-none"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Street / Area</label>
            <input
              type="text"
              value={form.location}
              onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
              placeholder="e.g. Kololo Hill Road"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">City *</label>
            <input
              type="text"
              value={form.city}
              onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
              placeholder="e.g. Kampala"
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Country</label>
          <input
            type="text"
            value={form.country}
            onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
            placeholder="e.g. Uganda"
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent"
          />
        </div>

        <div className="bg-gray-50 rounded-2xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-gray-900 text-sm">Bedrooms</div>
              <div className="text-xs text-gray-500">How many bedrooms?</div>
            </div>
            {counter('bedrooms', 0)}
          </div>
          <div className="border-t border-gray-200" />
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-gray-900 text-sm">Bathrooms</div>
              <div className="text-xs text-gray-500">How many bathrooms?</div>
            </div>
            {counter('bathrooms', 1)}
          </div>
          <div className="border-t border-gray-200" />
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold text-gray-900 text-sm">Max Guests</div>
              <div className="text-xs text-gray-500">Maximum number of guests</div>
            </div>
            {counter('maxGuests', 1)}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Step 3: Amenities ────────────────────────────────────────────────────────
function StepAmenities({ form, setForm }: { form: PropertyForm; setForm: React.Dispatch<React.SetStateAction<PropertyForm>> }) {
  const toggle = (a: string) => {
    setForm((f) => ({
      ...f,
      amenities: f.amenities.includes(a) ? f.amenities.filter((x) => x !== a) : [...f.amenities, a],
    }));
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-1" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
        What amenities do you offer?
      </h2>
      <p className="text-gray-500 text-sm mb-8">Select everything available at your property.</p>
      <div className="flex flex-wrap gap-3">
        {AMENITIES_LIST.map((a) => (
          <button
            key={a}
            onClick={() => toggle(a)}
            className={`px-4 py-2 rounded-full text-sm font-semibold border-2 transition-all ${
              form.amenities.includes(a)
                ? 'bg-rose-500 border-rose-500 text-white shadow-sm'
                : 'bg-white border-gray-200 text-gray-700 hover:border-rose-300'
            }`}
          >
            {a}
          </button>
        ))}
      </div>
      {form.amenities.length > 0 && (
        <div className="mt-6 p-4 bg-rose-50 rounded-xl border border-rose-100">
          <p className="text-sm font-semibold text-rose-700">{form.amenities.length} amenities selected</p>
          <p className="text-xs text-rose-500 mt-0.5">Properties with more amenities get 40% more bookings on average</p>
        </div>
      )}
    </div>
  );
}

// ─── Step 4: Pricing ──────────────────────────────────────────────────────────
function StepPricing({ form, setForm }: { form: PropertyForm; setForm: React.Dispatch<React.SetStateAction<PropertyForm>> }) {
  const estimatedMonthly = Math.round(form.pricePerNight * 22 * 0.75 + form.cleaningFee * 16);

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-1" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
        Set your pricing
      </h2>
      <p className="text-gray-500 text-sm mb-8">You can always adjust these later from your dashboard.</p>

      <div className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nightly Rate (UGX) *</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-semibold">UGX</span>
              <input
                type="number"
                value={form.pricePerNight || ''}
                onChange={(e) => setForm((f) => ({ ...f, pricePerNight: Number(e.target.value) }))}
                placeholder="0"
                className="w-full border border-gray-200 rounded-xl pl-14 pr-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Cleaning Fee (UGX)</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-semibold">UGX</span>
              <input
                type="number"
                value={form.cleaningFee || ''}
                onChange={(e) => setForm((f) => ({ ...f, cleaningFee: Number(e.target.value) }))}
                placeholder="0"
                className="w-full border border-gray-200 rounded-xl pl-14 pr-4 py-3 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-rose-400 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Minimum Nights</label>
          <div className="flex items-center gap-3">
            {[1, 2, 3, 5, 7].map((n) => (
              <button
                key={n}
                onClick={() => setForm((f) => ({ ...f, minNights: n }))}
                className={`px-4 py-2 rounded-xl text-sm font-bold border-2 transition-all ${
                  form.minNights === n
                    ? 'bg-rose-500 border-rose-500 text-white' :'bg-white border-gray-200 text-gray-700 hover:border-rose-300'
                }`}
              >
                {n} {n === 1 ? 'night' : 'nights'}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-2xl border border-gray-100">
          <div>
            <div className="font-semibold text-gray-900 text-sm">Instant Book</div>
            <div className="text-xs text-gray-500 mt-0.5">Guests can book without waiting for approval</div>
          </div>
          <button
            onClick={() => setForm((f) => ({ ...f, instantBook: !f.instantBook }))}
            className={`relative w-12 h-6 rounded-full transition-colors ${form.instantBook ? 'bg-rose-500' : 'bg-gray-300'}`}
          >
            <span
              className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                form.instantBook ? 'translate-x-6' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>

        {form.pricePerNight > 0 && (
          <div className="bg-gradient-to-br from-rose-50 to-orange-50 rounded-2xl p-5 border border-rose-100">
            <div className="text-xs font-bold text-rose-600 uppercase tracking-wide mb-3">Estimated Earnings</div>
            <div className="flex items-end gap-2">
              <span className="text-3xl font-extrabold text-gray-900">
                UGX {estimatedMonthly.toLocaleString()}
              </span>
              <span className="text-gray-500 text-sm mb-1">/month</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">Based on 75% occupancy at your nightly rate</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Step 5: Review & Publish ─────────────────────────────────────────────────
function StepReview({ form, setForm }: { form: PropertyForm; setForm: React.Dispatch<React.SetStateAction<PropertyForm>> }) {
  const selectedType = PROPERTY_TYPES.find((t) => t.id === form.type);
  const [selectedPhotos, setSelectedPhotos] = useState<string[]>(form.photos);

  const togglePhoto = (url: string) => {
    const updated = selectedPhotos.includes(url)
      ? selectedPhotos.filter((p) => p !== url)
      : [...selectedPhotos, url];
    setSelectedPhotos(updated);
    setForm((f) => ({ ...f, photos: updated }));
  };

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-1" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
        Review your listing
      </h2>
      <p className="text-gray-500 text-sm mb-8">Check everything looks right before publishing.</p>

      <div className="space-y-4">
        {/* Photos */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-gray-900 text-sm">Photos</h3>
            <span className="text-xs text-gray-400">Select photos to include</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {SAMPLE_PHOTOS.map((photo) => (
              <button
                key={photo.url}
                onClick={() => togglePhoto(photo.url)}
                className={`relative aspect-square rounded-xl overflow-hidden border-2 transition-all ${
                  selectedPhotos.includes(photo.url) ? 'border-rose-500' : 'border-transparent'
                }`}
              >
                <img src={photo.url} alt={photo.alt} className="w-full h-full object-cover" />
                {selectedPhotos.includes(photo.url) && (
                  <div className="absolute inset-0 bg-rose-500/20 flex items-center justify-center">
                    <div className="w-6 h-6 bg-rose-500 rounded-full flex items-center justify-center">
                      <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-3">
          <h3 className="font-bold text-gray-900 text-sm mb-4">Listing Summary</h3>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Type</span>
            <span className="font-semibold text-gray-900">{selectedType?.label || '—'}</span>
          </div>
          <div className="border-t border-gray-50" />
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Title</span>
            <span className="font-semibold text-gray-900 text-right max-w-[60%]">{form.title || '—'}</span>
          </div>
          <div className="border-t border-gray-50" />
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Location</span>
            <span className="font-semibold text-gray-900">{[form.city, form.country].filter(Boolean).join(', ') || '—'}</span>
          </div>
          <div className="border-t border-gray-50" />
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Capacity</span>
            <span className="font-semibold text-gray-900">{form.bedrooms} bed · {form.bathrooms} bath · {form.maxGuests} guests</span>
          </div>
          <div className="border-t border-gray-50" />
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Nightly Rate</span>
            <span className="font-semibold text-gray-900">
              {form.pricePerNight ? `UGX ${form.pricePerNight.toLocaleString()}` : '—'}
            </span>
          </div>
          <div className="border-t border-gray-50" />
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Amenities</span>
            <span className="font-semibold text-gray-900">{form.amenities.length} selected</span>
          </div>
          <div className="border-t border-gray-50" />
          <div className="flex justify-between text-sm">
            <span className="text-gray-500">Instant Book</span>
            <span className={`font-semibold ${form.instantBook ? 'text-emerald-600' : 'text-gray-500'}`}>
              {form.instantBook ? 'Enabled' : 'Disabled'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Success Screen ───────────────────────────────────────────────────────────
function SuccessScreen() {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">
        <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-3xl font-extrabold text-gray-900 mb-3" style={{ fontFamily: '"Plus Jakarta Sans", sans-serif' }}>
          Property Listed! 🎉
        </h2>
        <p className="text-gray-500 text-sm mb-8 leading-relaxed">
          Your property has been submitted and is under review. It will go live within 24 hours.
        </p>
        <div className="bg-white rounded-2xl border border-gray-100 p-5 mb-6 text-left space-y-3">
          {[
            { icon: '📸', title: 'Add more photos', desc: 'Listings with 10+ photos get 3× more bookings' },
            { icon: '📅', title: 'Set availability', desc: 'Open your calendar to start accepting reservations' },
            { icon: '💬', title: 'Write a welcome message', desc: 'Greet guests with a personal note' },
          ].map((item) => (
            <div key={item.title} className="flex gap-3 items-start">
              <span className="text-xl mt-0.5">{item.icon}</span>
              <div>
                <div className="font-semibold text-gray-900 text-sm">{item.title}</div>
                <div className="text-xs text-gray-500">{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
        <Link
          href="/host-dashboard"
          className="block w-full bg-rose-500 hover:bg-rose-600 text-white font-bold py-3.5 rounded-xl transition-colors text-sm"
        >
          Go to Host Dashboard
        </Link>
      </div>
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function AddPropertyPage() {
  const [step, setStep] = useState(1);
  const [published, setPublished] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [form, setForm] = useState<PropertyForm>({
    title: '',
    description: '',
    type: '',
    location: '',
    city: '',
    country: 'Uganda',
    bedrooms: 1,
    bathrooms: 1,
    maxGuests: 2,
    amenities: [],
    pricePerNight: 0,
    cleaningFee: 0,
    minNights: 1,
    instantBook: false,
    photos: [],
  });

  const canProceed = () => {
    if (step === 1) return !!form.type;
    if (step === 2) return !!form.title && !!form.city;
    if (step === 3) return true;
    if (step === 4) return form.pricePerNight > 0;
    return true;
  };

  const handlePublish = () => {
    setPublishing(true);
    setTimeout(() => {
      setPublishing(false);
      setPublished(true);
    }, 1800);
  };

  if (published) return <SuccessScreen />;

  const progress = ((step - 1) / (STEPS.length - 1)) * 100;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="pt-24 pb-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-6">
            <Link href="/host-dashboard" className="hover:text-rose-500 transition-colors font-medium">
              Host Dashboard
            </Link>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span className="text-gray-900 font-semibold">Add Property</span>
          </div>

          {/* Progress bar */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              {STEPS.map((s) => (
                <div key={s.id} className="flex flex-col items-center gap-1">
                  <div
                    className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                      step > s.id
                        ? 'bg-rose-500 text-white'
                        : step === s.id
                        ? 'bg-rose-500 text-white ring-4 ring-rose-100' :'bg-gray-200 text-gray-500'
                    }`}
                  >
                    {step > s.id ? (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      s.icon
                    )}
                  </div>
                  <span className={`text-xs font-semibold hidden sm:block ${step === s.id ? 'text-rose-500' : 'text-gray-400'}`}>
                    {s.label}
                  </span>
                </div>
              ))}
            </div>
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-rose-500 rounded-full transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex justify-between mt-1.5">
              <span className="text-xs text-gray-400">Step {step} of {STEPS.length}</span>
              <span className="text-xs text-gray-400">{Math.round(progress)}% complete</span>
            </div>
          </div>

          {/* Step content */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 md:p-8 mb-6">
            {step === 1 && <StepType form={form} setForm={setForm} />}
            {step === 2 && <StepDetails form={form} setForm={setForm} />}
            {step === 3 && <StepAmenities form={form} setForm={setForm} />}
            {step === 4 && <StepPricing form={form} setForm={setForm} />}
            {step === 5 && <StepReview form={form} setForm={setForm} />}
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            {step > 1 ? (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="flex items-center gap-2 px-5 py-3 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold text-sm hover:border-gray-300 transition-all"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
            ) : (
              <Link
                href="/host-dashboard"
                className="flex items-center gap-2 px-5 py-3 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold text-sm hover:border-gray-300 transition-all"
              >
                Cancel
              </Link>
            )}

            {step < STEPS.length ? (
              <button
                onClick={() => setStep((s) => s + 1)}
                disabled={!canProceed()}
                className={`flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-sm transition-all ${
                  canProceed()
                    ? 'bg-rose-500 hover:bg-rose-600 text-white shadow-sm hover:shadow-md'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }`}
              >
                Continue
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ) : (
              <button
                onClick={handlePublish}
                disabled={publishing}
                className="flex items-center gap-2 px-6 py-3 rounded-xl bg-rose-500 hover:bg-rose-600 text-white font-bold text-sm shadow-sm hover:shadow-md transition-all disabled:opacity-70"
              >
                {publishing ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Publishing...
                  </>
                ) : (
                  <>
                    🚀 Publish Listing
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
